import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, NgModule, NgZone, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InvoiceDataService } from '../services/invoice-data.service';
import { SiteDataService } from '../services/site-data.service';
import { ACHSettlementStatus, ACHSurchargeEnum, CardType, Invoice, Payment, PaymentLink, PaymentSystemEnum, Site, StarbaseServicesProviderBase, PaymentPendingService } from 'scaletech-core';
import { ForteService, PaymentService } from 'scaletech-payment';
import { DatePipe, formatDate } from '@angular/common';
import { StarbaseNotificationObject, StarbaseNotificationSentiment, StarbaseNotificationTypes } from 'starbase-notification';
import { StarbaseModalTypes } from 'starbase-modal-dialog';
import { share, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FormsModule } from '@angular/forms';

export interface CanComponentDeactivate {
  canDeactivate: () => boolean;
}

@Component({
  selector: 'app-make-payment',
  templateUrl: './make-payment.html',
  styleUrl: './make-payment.css',
  standalone: false
})
export class MakePaymentComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {
  paymentItemNumber: string;
  method: string;
  site: string;
  siteRecord: Site;
  invoiceRecord: Invoice;
  invoicePaid: boolean = false;
  dataLoaded: boolean = false;
  paymentRecord: Payment = new Payment();
  forteScript: string;
  inputedCode: string = "";
  displayPaymentId: string = "";


  // Forte Payment Related Fields
  private readonly FORTE_GLOBAL_CALLBACK_HANDLER_FN_NAME = "forteGlobalCallbackHandler";
  private readonly FORTE_ACTIVE_CALLBACK_HANDLER_FN_NAME = "forteActiveCallbackHandler";
  private static readonly forteIframeSelectors = [
    'iframe[src*="forte.net"]',
    'iframe[src*="sandbox.forte"]',
    'iframe[src*="checkout.forte"]',
    'iframe[src*="checkout-live-testlan"]',
    'iframe[src*="4ta.com"]',
  ];

  public forteError = false;
  public forteErrorMessage = '';
  public forteScriptLoaded = false;
  public processingForteRequest = false;

  public forteCountdownDisplay = '';
  public forteSessionNearExpiry = false;
  public forteSessionExpired = false;
  public forteCheckoutSignatureExpiry!: Date;
  private forteCountdownInterval: number | null = null;
  private achSurchargeTiers: number[][] = null; // forte ach surcharge/convenience fee charge rates

  private forteReloadInProgress = false;
  private forteScriptEl: HTMLScriptElement | null = null;
  private forteScriptLoadGen = 0;
  private forteCheckoutScriptEvaluated = false;
  private forteCheckoutOpenedAt = 0;
  private checkoutUiAbsentPoll: number | null = null;
  public savingErrorRetryCount = 0;
  forteCallbackEventDisplayedToUser = false; // Flag so webhook socket event and fortecallback dont double display the same error/success message


  @ViewChild('forteCheckoutButton') forteCheckoutButtonRef: ElementRef<HTMLButtonElement>;
  @ViewChild('verificationInput') verificationInput!: ElementRef<HTMLInputElement>;
  editablePaymentDate: any;
  isSaving: boolean = false;
  paymentUpdatedSubscription: Subscription;
  paymentExists: boolean = false;
  dataLoading: boolean = false;
  paymentComplete: boolean = false;
  allowedMethods: string[] = [];
  selectedMethod: string = "";
  jwt: string | null = null;
  recentACHFailure = false;

  hasUnsavedChanges = false;


  private subscriptionList: Subscription[] = [];
  isDownloading: any;
  get forteScriptURL() {
    return environment.FORTE_SCRIPT_URL;
  }

  constructor(private route: ActivatedRoute,
    public invoiceSvc: InvoiceDataService,
    public siteSvc: SiteDataService,
    private readonly ngZone: NgZone,
    private sbSvcProvider: StarbaseServicesProviderBase,
    private forteSvc: ForteService,
    private cd: ChangeDetectorRef,
    private paymentSvc: PaymentService,
    private payPendSvc: PaymentPendingService){

  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if(!this.paymentComplete && this.paymentExists){
      event.preventDefault();
      event.returnValue = '';
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.paymentItemNumber = params.paymentItemNumber;
      this.allowedMethods = (params.method ?? "")
        .split(",")
        .map(m => m.trim().toUpperCase());
      this.selectedMethod = this.allowedMethods[0] ?? "";
      this.site = params.site;
    });
  }

  private readonly onWindowFocus = (): void => {
    if (!this.processingForteRequest) {
      return;
    }

    window.setTimeout(() => {
      if (!this.processingForteRequest) {
        return;
      }

      if (Date.now() - this.forteCheckoutOpenedAt < 400) {
        return;
      }

      if (this.hasForteCheckoutUiOpen()) {
        return;
      }

      this.ngZone.run(() => {
        this.clearCheckoutUiAbsentPoll ();
        this.processingForteRequest = false;
        this.cleanupForteDomAfterForteClose ();
      });
    }, 80);
  };

  canDeactivate(): boolean {
    if (this.hasUnsavedChanges) {
      return confirm('You have unsaved changes. Leave this page?');
    }

    return true;
  }

  ngAfterViewInit(): void {
    this.verificationInput.nativeElement.focus();
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.paymentUpdatedSubscription) {
      this.paymentUpdatedSubscription.unsubscribe();
    }

    if (this.subscriptionList.length != 0){
      this.subscriptionList.forEach(sub => {
        sub.unsubscribe();
      });
    }

    if (this.siteRecord.paymentSystem === PaymentSystemEnum.FORTE) {
      window.removeEventListener('focus', this.onWindowFocus);
      this.clearCheckoutUiAbsentPoll ();
      this.clearSessionCountdown ();
      this.forteCheckoutScriptEvaluated = false;
      this.unloadForteScript ();

      delete (window as any)[this.FORTE_ACTIVE_CALLBACK_HANDLER_FN_NAME];
    }
  }

  public getInvoiceSiteData(): void {
    this.dataLoading = true;
    this.invoiceSvc.verifyPaymentLinkSessionSecureKey(parseInt(this.site), parseInt(this.paymentItemNumber), this.inputedCode).subscribe(({invoice,jwt}) => {

      this.invoiceRecord = invoice;
      this.siteRecord = invoice.job.site;
      this.jwt = jwt;
      this.dataLoaded = true;
      this.hasUnsavedChanges = true;
      const recentPayment = [...this.invoiceRecord.payments].sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()).at(-1);
      if(recentPayment){
        this.recentACHFailure = recentPayment.achSettlementStatus == ACHSettlementStatus.Rejected && !recentPayment.paymentVoided;
        if(this.recentACHFailure){
          this.jwt = null;
          this.dataLoaded = false;
          this.paymentExists = true;
          return;
        }
      }
      this.invoiceRecord.payments = this.invoiceRecord.payments.filter(p => !p.paymentVoided && p.awaitingCustomerAction === true);
      this.paymentRecord = this.invoiceRecord.payments[0] || new Payment();

      if (this.siteRecord.paymentSystem === PaymentSystemEnum.FORTE) {
      this.subscriptionList.push(this.payPendSvc.ForteWebhookReceivedTrigger$.subscribe(data => {
        if (!this.paymentRecord || data.siteUID !== this.siteRecord.uid || this.paymentRecord.paymentNumber !== data.paymentNumber) return;
        console.info("Forte Webhook Processed: ");
        console.table(data);
        this.paymentComplete = data.paymentSuccess;
        this.processingForteRequest = !data.paymentSuccess;
        this.paymentRecord.paymentPending = !data.paymentSuccess;
        if(data.paymentSuccess){
          this.getPaymentReceipt();
          const marker = "Transaction ID:";
          const index = data.message.indexOf(marker);

          this.displayPaymentId = index >= 0
          ? data.message.substring(index + marker.length).trim()
          : null;
        }
        this.displayForteCallbackEvent(data.paymentSuccess ? "Payment Successful" : "Payment Failed", data.message, !data.paymentSuccess);
      }));
    }
      let paymentChange = false
      if (this.invoiceRecord.amountRemaining <= 0) {
        if(this.paymentRecord.uid != 0){
          this.dataLoaded = false;
          this.paymentExists = true;
          this.paymentRecord = this.invoiceRecord.payments.find(p => p.awaitingCustomerAction == true);
          if(this.selectedMethod === "ACH" && this.paymentRecord.externalACHPayment === false || this.selectedMethod === "CARD" && this.paymentRecord.externalACHPayment === true){
            paymentChange = true;
            this.changePaymentMethod();
          }
          if (this.siteRecord.paymentSystem === PaymentSystemEnum.FORTE && !paymentChange) {
            window.addEventListener('focus', this.onWindowFocus);
            this.generateForteCheckoutButtonSignature();
          }
        } else {
          this.sbSvcProvider.notificationSvc.postNotification(this.sbSvcProvider.notificationSvc.notificationBuilder('Info', 'Invoice has already been paid.', StarbaseNotificationTypes.Info));
          this.invoicePaid = true;
          this.dataLoaded = false;
          this.paymentExists = true;
        }

      } else {
        if (this.invoiceRecord.payments.some(p => p.awaitingCustomerAction == true)) {
          this.paymentExists = true;
          this.paymentRecord = this.invoiceRecord.payments.find(p => p.awaitingCustomerAction == true);
          if (this.siteRecord.paymentSystem === PaymentSystemEnum.FORTE) {
            window.addEventListener('focus', this.onWindowFocus);
            this.generateForteCheckoutButtonSignature();
          }
        } else {
          this.preparePaymentForForte();
        }
      }
    });
  }

  changePaymentMethod(): void {
    if(this.selectedMethod === "ACH" && this.paymentRecord.externalACHPayment === false){
      this.paymentRecord.externalACHPayment = true;
      this.paymentRecord.achAmount = this.paymentRecord.cardAmount;
      this.paymentRecord.cardAmount = 0;
      this.paymentRecord.addedTaxAmount = 0;
      this.paymentRecord.achSurchargeAmount = this.paymentRecord.cardSurchargeAmount;
      this.paymentRecord.cardSurchargeAmount = 0;
      this.paymentRecord.cardSurchargeFlatRate = 0;
      this.paymentRecord.cardSurchargePercentage = 0;
      this.paymentRecord.cardSurchargeTaxRate = 0;
    } else if(this.selectedMethod === "CARD" && this.paymentRecord.externalACHPayment === true){
      this.paymentRecord.externalACHPayment = false;
      this.paymentRecord.cardAmount = this.paymentRecord.achAmount;
      this.paymentRecord.cardSurchargeAmount = this.paymentRecord.achSurchargeAmount;
      this.paymentRecord.achAmount = 0;
      this.paymentRecord.addedTaxAmount = 0;
      this.paymentRecord.achSurchargeAmount = 0;
      this.paymentRecord.achSurchargeFlatRate = 0;
      this.paymentRecord.achSurchargePercentage = 0;
      this.paymentRecord.achSurchargeTaxRate = 0;
    }
    this.updatePrepayment();
  }
  updatePrepayment(){
    this.paymentSvc.updatePaymentAmountsRemote(this.paymentRecord, this.jwt).subscribe(data => {
      this.paymentRecord = data;
      if (this.siteRecord.paymentSystem === PaymentSystemEnum.FORTE) {
        window.addEventListener('focus', this.onWindowFocus);
        this.generateForteCheckoutButtonSignature();
      }
    });
  }

  public preparePaymentForForte(): void {
    if(!this.jwt){
      //TODO:notification YOU SHOULD NOT BE HERE & send them back
      return;
    }
    this.paymentRecord = new Payment()
    this.paymentRecord.invoices ??= [];
    this.paymentRecord.invoices.push(this.invoiceRecord);
    this.paymentRecord.location = null;
    this.paymentRecord.customer = this.paymentRecord.customer ? this.paymentRecord.customer : this.paymentRecord.invoices[0].customer;
    this.paymentRecord.totalAmountDue = this.paymentRecord.invoices.map(i => i.amountRemaining).reduce((x, y) => x + y);
    this.paymentRecord.totalAmountDue.toFixed(2);
    this.paymentRecord.paymentLinks = this.paymentRecord.invoices.map(i => {
      let link = new PaymentLink();
      link.invoice = i;
      link.invoiceUID = i.uid;
      link.paymentAmount = i.amountRemaining;
      link.paymentAmountInput = link.paymentAmount.toFixed(2);
      link.amountRemainingBeforePayment = i.amountRemaining;
      link.amountRemainingAfterPayment = 0;
      return link;
    });
    this.paymentRecord.achAmount = 0;
    this.paymentRecord.cardAmount = (this.paymentRecord.cardAmount ? this.paymentRecord.cardAmount : 0);
    this.paymentRecord.cardName = (this.paymentRecord.cardName && this.paymentRecord.cardName.length > 0 ? this.paymentRecord.cardName : null);
    this.paymentRecord.cardType = this.paymentRecord.cardType;
    this.paymentRecord.cardSurchargeAmount = (this.paymentRecord.cardSurchargeAmount ? this.paymentRecord.cardSurchargeAmount : 0);
    this.paymentRecord.cardSurchargeFlatRate = (this.paymentRecord.cardSurchargeFlatRate ? this.paymentRecord.cardSurchargeFlatRate : 0);
    this.paymentRecord.cardSurchargePercentage = (this.paymentRecord.cardSurchargePercentage ? this.paymentRecord.cardSurchargePercentage : 0);
    this.paymentRecord.cardSurchargeTaxRate = (this.paymentRecord.cardSurchargeTaxRate ? this.paymentRecord.cardSurchargeTaxRate : 0);
    this.paymentRecord.achSurchargeAmount = (this.paymentRecord.achSurchargeAmount ? this.paymentRecord.achSurchargeAmount : 0);
    this.paymentRecord.achSurchargePercentage = (this.paymentRecord.achSurchargePercentage ? this.paymentRecord.achSurchargePercentage : 0);
    this.paymentRecord.achSurchargeFlatRate = (this.paymentRecord.achSurchargeFlatRate ? this.paymentRecord.achSurchargeFlatRate : 0);
    this.paymentRecord.achSurchargeTaxRate = (this.paymentRecord.achSurchargeTaxRate ? this.paymentRecord.achSurchargeTaxRate : 0);
    if(this.selectedMethod === "ACH") {
      this.paymentRecord.externalACHPayment = true;
      this.paymentRecord.achSettlementStatus = ACHSettlementStatus.NotStarted;
      this.paymentRecord.achAmount = this.paymentRecord.totalAmountDue;
    } else if (this.selectedMethod === "CARD") {
      this.paymentRecord.externalACHPayment = false;
      this.paymentRecord.cardAmount = this.paymentRecord.totalAmountDue;
      this.paymentRecord.cardName = null;
      this.paymentRecord.cardType = 0;
    }
    this.paymentRecord.cardAmount = (this.paymentRecord.cardAmount ? this.paymentRecord.cardAmount : 0);
    this.paymentRecord.cardName = (this.paymentRecord.cardName && this.paymentRecord.cardName.length > 0 ? this.paymentRecord.cardName : null);
    this.paymentRecord.cardType = this.paymentRecord.cardType;
    this.paymentRecord.checkAmount = 0;
    this.paymentRecord.checkName = null;
    this.paymentRecord.checkNumber = null;
    this.paymentRecord.appliedCredit = 0;
    this.paymentRecord.addedTaxAmount = (this.paymentRecord.addedTaxAmount ? this.paymentRecord.addedTaxAmount : 0);
    this.paymentRecord.achTaxAmount = (this.paymentRecord.achTaxAmount ? this.paymentRecord.achTaxAmount : 0);
    this.paymentRecord.voucherAmount = 0;
    this.paymentRecord.voucherUsed = false;
    this.paymentRecord.voucherAccount = null;
    this.paymentRecord.voucherAccountUID = null;
    this.paymentRecord.awaitingCustomerAction = true;
    this.paymentRecord.totalAmountDue = this.paymentRecord.invoices.map(i => i.amountRemaining).reduce((x, y) => x + y) + this.paymentRecord.cardSurchargeAmount + this.paymentRecord.achSurchargeAmount + this.paymentRecord.addedTaxAmount + this.paymentRecord.achTaxAmount;
    this.paymentRecord.totalAmountPaid = this.paymentRecord.totalAmountDue;
    this.paymentRecord.amountRemaining = this.paymentRecord.amountRemaining;
    this.paymentRecord.paymentDate = new Date();
    this.paymentRecord.notes = null;
    this.paymentRecord.paymentLinks = this.paymentRecord.paymentLinks;
    this.paymentRecord.cashBack = 0;
    this.paymentRecord.accountCredit = this.paymentRecord.accountCredit;
    this.paymentRecord.totalAmountDue = this.paymentRecord.totalAmountDue;
    this.paymentRecord.paymentPending = true;

    this.paymentSvc.createPaymentForACH(this.paymentRecord,this.jwt).subscribe(result => {
      this.paymentRecord = result;
      this.paymentRecord.customer = this.invoiceRecord.customer;
      //this.editablePaymentDate = this.paymentDateString(this.paymentRecord.paymentDate);
      this.setVariablesToFalse();
      this.sbSvcProvider.notificationSvc.postNotification(this.sbSvcProvider.notificationSvc.notificationBuilder('Success', 'Verification Code Accepted', StarbaseNotificationTypes.Success));
      this.cd.detectChanges();
      this.paymentExists = true;
      if (this.siteRecord.paymentSystem === PaymentSystemEnum.FORTE) {
        window.addEventListener('focus', this.onWindowFocus);
        this.generateForteCheckoutButtonSignature();
      }
    }, (err) => {
      console.error('Error with Verification Code Processing:', err);
      this.sbSvcProvider.notificationSvc.postNotification(this.sbSvcProvider.notificationSvc.notificationBuilder('Error', 'Error with Verification Code Processing, please try again later ', StarbaseNotificationTypes.Error));
    });
  }

  private clearSessionCountdown(): void {
    if (this.forteCountdownInterval !== null) {
      clearInterval(this.forteCountdownInterval);
      this.forteCountdownInterval = null;
    }
  }

  private startForteCountdown(): void {
    const expires = new Date(Date.now() + 15 * 60 * 1000);
    this.forteCheckoutSignatureExpiry = expires;
    this.clearSessionCountdown();
    const tick = (): void => {
      const s = Math.max(0, Math.floor((expires.getTime() - Date.now()) / 1000));
      this.forteCountdownDisplay = `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
      this.forteSessionNearExpiry = s > 0 && s < 120;
      this.forteSessionExpired = s === 0;
    };

    tick ();
    this.forteCountdownInterval = window.setInterval(tick, 1000);
  }

  private resetForteCheckoutStatuses(): void {
    this.forteError = false;
    this.forteErrorMessage = '';
    this.forteScriptLoaded = false;
    this.processingForteRequest = false;
    this.clearSessionCountdown();
    this.forteCountdownDisplay = '';
    this.forteSessionNearExpiry = false;
    this.forteSessionExpired = false;
  }

  private resetForteCheckoutStatusesAfterAbort (): void {
    this.forteError = false;
    this.forteErrorMessage = '';
    this.processingForteRequest = false;
    this.clearSessionCountdown();
    this.forteCountdownDisplay = '';
    this.forteSessionNearExpiry = false;
    this.forteSessionExpired = false;
  }

  private hasForteCheckoutFrame(): boolean {
    return MakePaymentComponent.forteIframeSelectors.some(
      (sel) => document.querySelector(sel) !== null
    );
  }

  private hasForteCheckoutUiOpen(): boolean {
    if (document.querySelector('.fco2-iframe-container, #fco-embedded')) {
      return true;
    }

    return this.hasForteCheckoutFrame();
  }

  private clearCheckoutUiAbsentPoll(): void {
    if (this.checkoutUiAbsentPoll !== null) {
      clearInterval(this.checkoutUiAbsentPoll);
      this.checkoutUiAbsentPoll = null;
    }
  }

  private startCheckoutUiAbsentPoll(): void {
    this.clearCheckoutUiAbsentPoll();
    this.checkoutUiAbsentPoll = window.setInterval(() => {
      if (!this.processingForteRequest) {
        this.clearCheckoutUiAbsentPoll();
        return;
      }

      if (Date.now() - this.forteCheckoutOpenedAt < 400) {
        return;
      }

      if (this.hasForteCheckoutUiOpen()) {
        return;
      }

      this.ngZone.run(() => {
        this.processingForteRequest = false;
        this.cleanupForteDomAfterForteClose();
        this.clearCheckoutUiAbsentPoll();
      });
    }, 120);
  }

  private cleanupForteDomAfterForteClose(): void {
    document.body.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('overflow');

    document.querySelectorAll('.fco2-iframe-container, #fco-embedded').forEach((el) => {
      el.remove();
    });

    document.querySelectorAll('form[id^="f"][method="POST"]').forEach((el) => {
      if (el.getAttribute('action')?.includes('4ta.com')) {
        el.remove();
      }
    });

    MakePaymentComponent.forteIframeSelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => el.remove());
    });
  }

  private scheduleForteReloadAfterError(): void {
    if (this.forteReloadInProgress) {
      return;
    }

    this.forteReloadInProgress = true;

    setTimeout(() => {
      this.cleanupForteDomAfterForteClose();
      this.stripScriptsAppendedAfterHostButton();
      this.removeForteButtonAttributes();

      setTimeout(() => {
        this.forteReloadInProgress = false;
        void this.generateForteCheckoutButtonSignature();
      }, 200);
    }, 0);
  }

  // Simple helper function to handle successful forte payments, and update this record with valid error retry logic
  //TODO: Refactor error handling to rely on webhook/socket events
  //if websocket fails requst to server to see if payment has been made by comparing payment models
  private saveSuccessfulFortePayment(checkoutData: any) {
    this.processingForteRequest = true;
    const externalPaymentId = checkoutData.trace_number;
    this.displayPaymentId = externalPaymentId;
    this.paymentRecord.externalPaymentId = externalPaymentId;

    if (this.savingErrorRetryCount > 2) {
      this.forteErrorMessage = "Payment was processed successfully (" + externalPaymentId + "), but saving the record failed. Unable to save the record after multiple attempts. Please contact our support for assistance.";
      this.sbSvcProvider.notificationSvc.postNotification(
        this.sbSvcProvider.notificationSvc.notificationBuilder('Saving Record Failed', this.forteErrorMessage, StarbaseNotificationTypes.Error, 12_000
      ));

      return;
    }

    this.commitDataChangesToServer(async(success) => {
      if (success) {
        this.resetForteCheckoutStatuses();
        this.displayForteCallbackEvent("Successfuly processed payment", 'Payment was processed successfully. Transaction ID: ' + checkoutData.trace_number, false);
      }
    });

    this.getPaymentReceipt();
  }

  private displayForteCallbackEvent(label: string, message: string, err: boolean) {
    setTimeout(() => {
      if (this.forteCallbackEventDisplayedToUser) return;
      this.forteCallbackEventDisplayedToUser = true;

      setTimeout(() => this.forteCallbackEventDisplayedToUser = false, 30_000);
      this.forteError = err;
      this.forteErrorMessage = message;
      this.sbSvcProvider.notificationSvc.postNotification(this.sbSvcProvider.notificationSvc.notificationBuilder(label, message, err? StarbaseNotificationTypes.Error: StarbaseModalTypes.Success, 10_000));
    }, 300);
  }

  async generateForteCheckoutButtonSignature() {
    const globalForteHandler = (window as any)[this.FORTE_GLOBAL_CALLBACK_HANDLER_FN_NAME];
    if (!globalForteHandler || typeof globalForteHandler !== "function") {
      (window as any)[this.FORTE_GLOBAL_CALLBACK_HANDLER_FN_NAME] = (ev: any) => {
        const activeHandler = (window as any)[this.FORTE_ACTIVE_CALLBACK_HANDLER_FN_NAME];
        if (activeHandler && typeof activeHandler === "function") {
          activeHandler(ev);
        }
      };
    }

    this.cleanupForteDomAfterForteClose();
    if (this.forteCheckoutScriptEvaluated) {
      this.resetForteCheckoutStatusesAfterAbort();
    } else {
      this.resetForteCheckoutStatuses();
      this.unloadForteScript();
    }

    this.removeForteButtonAttributes();

    // https://support.forte.net/support/solutions/articles/11000126268-convenience-service-fee-and-surcharge-tip-sheet
    const amount = this.paymentRecord.externalACHPayment ? this.paymentRecord.achAmount
      : this.paymentRecord.cardAmount;
    this.forteSvc.generateCheckoutSignatureForCustomerACH({ CustomerUID: this.paymentRecord.customer.uid, Amount: amount }, this.jwt).subscribe((checkoutData) => {
      this.stripScriptsAppendedAfterHostButton ();

      (window as any)[this.FORTE_ACTIVE_CALLBACK_HANDLER_FN_NAME] = (ev: unknown) => {
        this.ngZone.run(() => {
          const raw = ev as { data?: unknown };
          let data: {
            event?: string;
            response_code?: string;
            response_description?: string;
            response_desc?: string;
            trace_number?: string;
            billing_name?: string;
            name_on_card?: string;
            method_used?: string;
          };
          try {
            data = typeof raw?.data === 'string' ? (JSON.parse(raw.data) as typeof data) : (raw as typeof data);
          } catch {
            return;
          }

          if (!data || typeof data.event !== 'string') {
            return;
          }

          if (data.event === 'begin') {
            this.forteCheckoutOpenedAt = Date.now();
            return;
          }

          else if (data.event === 'success') {
            this.processingForteRequest = true;

            if (this.paymentRecord.externalACHPayment) { // ACH Payment Handling
              //this.paymentRecord.paymentDate = new Date(this.editablePaymentDate);
              this.paymentRecord.paymentPending = true;
              this.paymentRecord.achSettlementStatus = ACHSettlementStatus.Settling;
              this.paymentRecord.achCustomerName = data.billing_name;
              this.clearCheckoutUiAbsentPoll ();
              this.processingForteRequest = false;
              this.cleanupForteDomAfterForteClose ();
              return this.saveSuccessfulFortePayment(data);
            }

            this.jwt = null;
          }

          else if (data.event === 'failure' || data.event === 'error') {
            this.clearCheckoutUiAbsentPoll ();
            this.processingForteRequest = false;
            this.scheduleForteReloadAfterError();
            return this.displayForteCallbackEvent("Payment Failed", `Payment collection failed [${data?.response_code ?? 'ERR'}] ${data?.response_description ?? data.response_desc}`, true);
          }

          else if (data.event === 'abort') {
            this.clearCheckoutUiAbsentPoll ();
            this.processingForteRequest = false;
            this.cleanupForteDomAfterForteClose ();
            queueMicrotask(() => void this.generateForteCheckoutButtonSignature());
            return;
          }

          else if (data.event === 'expired') {
            this.clearCheckoutUiAbsentPoll ();
            queueMicrotask(() => void this.generateForteCheckoutButtonSignature());
          }
        });
      };
      
      /* X_Data fields:
        xdata_1: Payment Number
        xdata_2: SiteCode
        xdata_3: LocationName | Web | undef
      */

      const btn = this.forteCheckoutButtonRef.nativeElement;
      btn.setAttribute("xdata_1", this.paymentRecord.paymentNumber.toString()); // Payment.PaymentNumber
      btn.setAttribute("xdata_2", this.siteRecord.siteCode); // Site.SiteCode
      btn.setAttribute("xdata_3", "Web"); // paid with this web portal

      btn.setAttribute("api_access_id", checkoutData.apiAccessId);
      btn.setAttribute("version_number", checkoutData.versionNumber);
      btn.setAttribute("location_id", checkoutData.locationId);
      btn.setAttribute("method", checkoutData.method);
      btn.setAttribute("total_amount", checkoutData.totalAmount);
      btn.setAttribute("utc_time", checkoutData.utcTime);
      btn.setAttribute("customer_token", checkoutData.customerToken);

      btn.setAttribute("signature", checkoutData.signature);
      btn.setAttribute("callback", this.FORTE_GLOBAL_CALLBACK_HANDLER_FN_NAME);
      btn.setAttribute("hash_method", "sha256");
      btn.setAttribute("entered_by", "ScaleTech_ACH_Portal");

      if (this.paymentRecord.externalACHPayment && this.paymentRecord.achAmount > 0) {
        btn.setAttribute("allowed_methods", "echeck");
        btn.setAttribute("swipe", "false");
      } else if (this.paymentRecord.cardAmount > 0) {
        btn.setAttribute("allowed_methods", "visa,mast,disc,amex,dine");
      }

      // Hide Billing Parameters
      btn.setAttribute("collect_shipping_address", "false");
      btn.setAttribute("billing_street_line1_attr", "hide");
      btn.setAttribute("billing_street_line2_attr", "hide");
      btn.setAttribute("billing_locality_attr", "hide");
      btn.setAttribute("billing_region_attr", "hide");
      btn.setAttribute("billing_postal_code_attr", "hide");
      btn.setAttribute("billing_country_attr", "hide");
      btn.setAttribute("billing_email_address_attr", "hide");
      btn.setAttribute("billing_phone_number_attr", "hide");

      this.reloadForteAPIScript();
      this.startForteCountdown();
    });
  }

  private withCacheBust(scriptUrl: string): string {
    try {
      const u = new URL(scriptUrl, window.location.href);
      u.searchParams.set('_', String(Date.now()));
      return u.toString();
    } catch {
      const sep = scriptUrl.includes('?') ? '&' : '?';
      return `${scriptUrl}${sep}_=${Date.now()}`;
    }
  }

  private unloadForteScript(): void {
    this.forteScriptLoaded = false;
    let hostHint = '';
    try {
      hostHint = new URL(this.forteScriptURL, window.location.href).hostname;
    } catch {
      /* ignore */
    }

    document.querySelectorAll('script[src]').forEach((node) => {
      const s = node as HTMLScriptElement;
      if (
        s.id === 'forte-script' ||
        (hostHint && s.src.includes(hostHint)) ||
        s.src.includes('checkout-live-testlan') ||
        s.src.includes('4ta.com')
      ) {
        s.remove();
      }
    });

    document.getElementById('forte-script')?.remove();
    this.forteScriptEl?.remove();
    this.forteScriptEl = null;
  }

  private stripScriptsAppendedAfterHostButton(): void {
    const btn = this.forteCheckoutButtonRef?.nativeElement;
    if (!btn) {
      return;
    }
    for (let n = btn.nextSibling; n && n.nodeName === 'SCRIPT'; ) {
      const remove = n;
      n = n.nextSibling;
      remove.remove();
    }
  }

  private removeForteButtonAttributes(): void {
    const btn = this.forteCheckoutButtonRef?.nativeElement;
    if (!btn) {
      return;
    }
    const keep = new Set(['class', 'type', 'style']);
    Array.from(btn.attributes).forEach((attr) => {
      if (!keep.has(attr.name)) {
        btn.removeAttribute(attr.name);
      }
    });
  }

  //Load Forte script only once and reuse it for all sessions
  private reloadForteAPIScript(): void {
    if (this.forteCheckoutScriptEvaluated) {
      this.forteScriptLoaded = true;
      return;
    }

    this.unloadForteScript();
    const gen = ++this.forteScriptLoadGen;
    const script = document.createElement('script');
    script.id = 'forte-script';
    script.src = this.withCacheBust(this.forteScriptURL);
    script.async = true;

    script.onload = () => {
      if (gen !== this.forteScriptLoadGen) {
        return;
      }
      this.forteCheckoutScriptEvaluated = true;
      this.forteScriptLoaded = true;
    };

    document.body.appendChild(script);
    this.forteScriptEl = script;
  }

  public onPayWithForteClick(): void {
    const host = this.forteCheckoutButtonRef?.nativeElement;
    if (!host || !this.forteScriptLoaded || this.processingForteRequest) {
      return;
    }

    this.forteCheckoutOpenedAt = Date.now();
    this.processingForteRequest = true;
    this.startCheckoutUiAbsentPoll();
    host.click();
  }

  paymentDateString(d: Date | string): string {
    const date = d instanceof Date ? d : new Date(d);
    return formatDate(date, 'yyyy-MM-ddTHH:mm', 'en-us');
  }

  commitDataChangesToServer(callback?: (success: boolean) => void): void {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    let tempInvoices = this.paymentRecord.invoices;
    this.paymentRecord.invoices = null;
    let tempLinks = this.paymentRecord.paymentLinks;
    this.paymentRecord.paymentLinks = null;

    const temp = structuredClone(this.paymentRecord);
    if (temp.uid > 0) {
      this.paymentSvc.updateACH(this.paymentRecord.uid, temp, this.jwt).subscribe(result => {
        this.paymentRecord = result;
        if (this.paymentRecord.paymentLinks?.length > tempLinks.length){
          this.paymentRecord.paymentLinks = tempLinks
        }

        //this.paymentRecord.scaleTicketStatusDisp = scaleTicketStatuses.find(x => x.status === this.paymentRecord.scaleTicketStatus);
        this.setVariablesToFalse();
        this.sbSvcProvider.notificationSvc.postNotification(this.sbSvcProvider.notificationSvc.notificationBuilder('Success', 'Record updated successfully!', StarbaseNotificationTypes.Success));
        this.cd.detectChanges();
        //this.saveChangesEmitter.emit(result.value);
        setTimeout(() => this.isSaving = false, 500);
        this.paymentComplete = true;
        this.jwt = null;
        if (callback) {
          callback(true);
        }
      }, (err) => {
        console.error('Error updating the record:', err);
        this.paymentRecord.paymentLinks = tempLinks;
        this.paymentRecord.invoices = tempInvoices;
        this.sbSvcProvider.notificationSvc.postNotification(this.sbSvcProvider.notificationSvc.notificationBuilder('Error','Error updating the record, please try again later ', StarbaseNotificationTypes.Error));
        this.isSaving = false;
        if (callback) {
          callback(false);
        }
      });
    }
  }

  setVariablesToFalse() {
    this.resetForteCheckoutStatuses();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pasted = event.clipboardData?.getData('text') ?? '';

    this.inputedCode = pasted
      .replace(/\s+/g, '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .substring(0, 6);
  }

  onCodeChange(value: string): void {
    this.inputedCode = value
      .trim()
      .replace(/\s+/g, '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .substring(0, 6);
  }

  getPaymentReceipt(): void{
    console.error("Please implement this!!!");
    return;
    if (!this.isDownloading) {
      this.isDownloading = true;
      const getReceipt$ = this.paymentSvc.createPaymentReceiptCustomerACH(this.paymentRecord.uid, this.jwt).pipe(share());
      const notifier = new StarbaseNotificationObject(
        'Downloading...',
        null,
        StarbaseNotificationSentiment.Notice,
        null,
        getReceipt$);
      getReceipt$.subscribe(
        (content: any) => {
          const blob = new Blob([content], { type: 'application/pdf' });
          const downLink = document.createElement('a');
          downLink.href = window.URL.createObjectURL(blob);
          downLink.setAttribute('download', `Payment Receipt ${new DatePipe('en-US').transform(new Date(), 'yyyy-MM-dd-HH-mm')}.pdf`);
          downLink.click();
          window.open(downLink.href, '_blank');
          this.isDownloading = false;
          this.cd.detectChanges();
          notifier.updateTitle('Success');
          notifier.updateContent('Receipt Successfully Generated');
        },
        (e) => {
          console.error(e);
          this.isDownloading = false;
          this.cd.detectChanges();
          notifier.updateTitle('Error');
          if (e.status === 404) {
            notifier.updateContent('No Data to Report')
          }
          else {
            notifier.updateContent('Failed to Generate Report');
          }
        }
      );
      this.sbSvcProvider.notificationSvc.postNotification(notifier);
    }
  }
}
